'use client';

import { useState } from 'react';
import Link from 'next/link';
import AnchorLink from '@/components/TableOfContents/AnchorLink';
import ContentfulRichText from '@/components/ContentfulRichText/ContentfulRichText';
import { useForm } from 'react-hook-form';
import { InputMask } from '@react-input/mask';
import {
  getPatterns,
  getMinValues,
  getMaxValues,
  stateOptions,
} from '@/utils/formUtils';
import { analytics } from '@/utils/analytics';
import { submitContactForm } from '@/services/formService';
import { DescriptionType } from '@/utils/common.types';

interface Props {
  title: string;
  description?: DescriptionType;
  includeSegment?: boolean;
}

const CtaContactForm = ({ title, description, includeSegment }: Props) => {
  const [isFormSending, setIsFormSending] = useState(false);
  const [formStatus, setFormStatus] = useState<'success' | 'error' | null>(
    null
  );

  const anchorTagName = 'contact-us-form';

  const {
    register,
    handleSubmit,
    getValues,
    setError,
    formState: { errors },
  } = useForm();

  const generateErrorMsg = (errors, input) => {
    let hasErrorProperty = Object.prototype.hasOwnProperty.call(errors, input);
    if (hasErrorProperty) {
      return (
        <p className="absolute -bottom-[1px] left-2.5 translate-y-3 pt-1 text-[0.85rem] leading-4 text-red-500">
          {errors[input].message}
        </p>
      );
    }
  };

  const onSubmit = async () => {
    const formValues = getValues();
    if (formValues.email !== formValues.email2) {
      setError('email', {
        type: 'value',
        message: 'E-mail address must match.',
      });
      setError('email2', {
        type: 'value',
        message: 'E-mail address must match.',
      });
    } else {
      setIsFormSending(true);
      await submitContactForm(formValues).then((status) => {
        if (status === 200) {
          setFormStatus('success');
          includeSegment &&
            analytics?.track('Generic Contact Form Submitted', {
              formId: 'contact-form',
              email: formValues.email,
              firstName: formValues.firstName,
              lastName: formValues.lastName,
              phone: formValues.phone,
              comments: formValues.comments,
            });
        } else {
          setFormStatus('error');
        }
      });
    }
  };

  return (
    <div className="mx-auto my-5 w-mobile max-w-standard py-[30px]">
      <AnchorLink anchorTagRef={`#${anchorTagName}`} />
      {title && (
        <h3 className="mb-[15px] font-acumin text-[26px] font-semibold text-hbg-blue md:text-[36px]">
          {title}
        </h3>
      )}
      {description && (
        <div className="mb-5 text-hbg-blue">
          {formStatus === null && <div>{ContentfulRichText(description)}</div>}
        </div>
      )}
      <div>
        {formStatus === 'success' && (
          <div className="pb-[150px] pt-4 text-[30px] font-semibold text-green-500">
            We received your request. Thank you.
          </div>
        )}
        {formStatus === 'error' && (
          <div className="pb-[150px] pt-4 text-[30px] font-semibold text-red-500">
            Error. Sorry. We could not process your request.
          </div>
        )}
        {!formStatus && (
          <form
            onSubmit={handleSubmit(onSubmit)}
            autoComplete="on"
            id="contact-us-form"
            name="contact-us-form"
          >
            <div className="[@media(min-width:640px)]:grid [@media(min-width:640px)]:w-full [@media(min-width:640px)]:grid-cols-2 [@media(min-width:640px)]:gap-y-5">
              <div className="relative col-start-1 col-end-2 row-start-2 row-end-2 mx-auto my-[3px] flex w-full flex-col justify-between text-grey-1">
                <input
                  {...register('firstName', {
                    required: 'First name is required.',
                    pattern: getPatterns('firstName'),
                    minLength: getMinValues('firstName'),
                    maxLength: getMaxValues('firstName'),
                  })}
                  placeholder="First name*"
                  type="text"
                  className="my-2 w-full border-b-[1px] bg-white p-2 font-acumin text-[16px]"
                />
                {errors && generateErrorMsg(errors, 'firstName')}
              </div>
              <div className="relative col-start-2 col-end-3 row-start-2 row-end-2 mx-auto my-[3px] flex w-full flex-col justify-between text-grey-1">
                <input
                  {...register('lastName', {
                    required: 'Last name is required.',
                    pattern: getPatterns('lastName'),
                    minLength: getMinValues('lastName'),
                    maxLength: getMaxValues('lastName'),
                  })}
                  placeholder="Last name*"
                  type="text"
                  className="my-2 w-full border-b-[1px] bg-white p-2 font-acumin text-[16px] md:ml-5 md:w-[unset]"
                />
                {errors && generateErrorMsg(errors, 'lastName')}
              </div>
              <div className="relative col-start-1 col-end-3 row-start-3 row-end-3 mx-auto my-[3px] flex w-full flex-col justify-between text-grey-1">
                <InputMask
                  {...register('phone', {
                    required: 'Phone number is required.',
                    pattern: getPatterns('phone'),
                    minLength: getMinValues('phone'),
                    maxLength: getMaxValues('phone'),
                  })}
                  mask="___-___-____"
                  placeholder="Phone number*"
                  replacement="_"
                  type="text"
                  className="my-2 w-full border-b-[1px] bg-white p-2 font-acumin text-[16px]"
                />
                {errors && generateErrorMsg(errors, 'phone')}
              </div>
              <div className="relative col-start-1 col-end-3 row-start-4 row-end-4 mx-auto my-[3px] flex w-full flex-col justify-between text-grey-1">
                <input
                  {...register('email', {
                    required: 'E-mail address is required.',
                    pattern: getPatterns('email'),
                    minLength: getMinValues('email'),
                    maxLength: getMaxValues('email'),
                    validate: () => getValues('email2') === getValues('email'),
                  })}
                  placeholder="Email address*"
                  type="text"
                  className="my-2 w-full border-b-[1px] bg-white p-2 font-acumin text-[16px]"
                />
                {errors && generateErrorMsg(errors, 'email')}
              </div>
              <div className="relative col-start-1 col-end-3 row-start-5 row-end-5 mx-auto my-[3px] flex w-full flex-col justify-between text-grey-1">
                <input
                  {...register('email2', {
                    required: 'E-mail address is required.',
                    pattern: getPatterns('email'),
                    minLength: getMinValues('email'),
                    maxLength: getMaxValues('email'),
                  })}
                  placeholder="Confirm email address*"
                  type="text"
                  className="my-2 w-full border-b-[1px] bg-white p-2 font-acumin text-[16px]"
                />
                {errors && generateErrorMsg(errors, 'email2')}
              </div>
            </div>
            <div className="[@media(min-width:640px)]:grid [@media(min-width:640px)]:grid-cols-3 [@media(min-width:640px)]:gap-y-5">
              <div className="relative col-start-1 col-end-4 row-start-1 row-end-2 mx-auto my-[3px] flex w-full flex-col justify-between text-grey-1">
                <input
                  {...register('address', {
                    pattern: getPatterns('address'),
                    maxLength: getMaxValues('address'),
                  })}
                  placeholder="Street Address"
                  type="text"
                  className="my-2 w-full border-b-[1px] bg-white p-2 font-acumin text-[16px]"
                />
                {errors && generateErrorMsg(errors, 'address')}
              </div>
              <div className="relative col-start-1 col-end-2 row-start-2 row-end-3 mx-auto my-[3px] flex w-full flex-col justify-between text-grey-1">
                <input
                  {...register('city', {
                    pattern: getPatterns('city'),
                    maxLength: getMaxValues('city'),
                  })}
                  placeholder="City"
                  type="text"
                  className="my-2 w-mobile border-b-[1px] bg-white p-2 font-acumin text-[16px]"
                />
                {errors && generateErrorMsg(errors, 'city')}
              </div>
              <div className="relative col-start-2 col-end-3 row-start-2 row-end-3 mx-auto my-[3px] flex w-full flex-col justify-between text-grey-1">
                <select
                  {...register('state')}
                  name="state"
                  defaultValue=""
                  className="my-2 w-mobile border-b-[1px] bg-white p-2 font-acumin text-[16px] text-grey-1"
                >
                  <option disabled hidden value="">
                    State
                  </option>
                  {stateOptions?.map((state, index) => (
                    <option value={state?.abbreviation} key={index}>
                      {state?.name}
                    </option>
                  ))}
                </select>
                {errors && generateErrorMsg(errors, 'state')}
              </div>
              <div className="relative col-start-3 col-end-4 row-start-2 row-end-3 mx-auto my-[3px] flex w-full flex-col justify-between text-grey-1">
                <input
                  {...register('zip', {
                    pattern: getPatterns('zip'),
                    maxLength: getMaxValues('zip'),
                  })}
                  placeholder="Zip"
                  type="text"
                  className="my-2 w-mobile border-b-[1px] bg-white p-2 font-acumin text-[16px]"
                />
                {errors && generateErrorMsg(errors, 'zip')}
              </div>
            </div>
            <div className="[@media(min-width:640px)]:grid [@media(min-width:640px)]:grid-cols-3 [@media(min-width:640px)]:gap-y-5">
              <div className="relative col-start-1 col-end-4 row-start-2 row-end-3 mx-auto my-[3px] flex w-full flex-col justify-between text-grey-1">
                <textarea
                  {...register('comments', {
                    pattern: getPatterns('comments'),
                    maxLength: getMaxValues('comments'),
                  })}
                  placeholder="Please type your question or comment (max 500 characters)."
                  className="my-2 w-full border-b-[1px] bg-white p-2 pb-[75px] font-acumin text-[16px]"
                />
                {errors && generateErrorMsg(errors, 'comments')}
              </div>
            </div>
            <div>
              <div className="relative mx-auto mb-10 flex w-full flex-row gap-4">
                <input
                  {...register('consent', {
                    required:
                      'Please check the box to agree to the Terms & Conditions.*',
                  })}
                  type="checkbox"
                  id="consent"
                  name="consent"
                  placeholder="checked"
                  defaultChecked={false}
                />
                <p className="mr-2.5">
                  By checking this box, I agree to Clayton&apos;s{' '}
                  <Link
                    href="https://www.claytonhomes.com/legal/"
                    target="_blank"
                    rel="noreferrer noopenner"
                  >
                    Legal Policy
                  </Link>{' '}
                  and{' '}
                  <Link
                    href="https://www.claytonhomes.com/privacy/"
                    target="_blank"
                    rel="noreferrer noopenner"
                  >
                    Privacy Policy
                  </Link>
                  .
                </p>
                {errors && generateErrorMsg(errors, 'consent')}
              </div>
            </div>
            <div className="mx-auto my-[30px] w-full cursor-pointer rounded-[3px] border-none">
              <button
                type="submit"
                id="submit-button"
                className="w-full cursor-pointer rounded-[3px] border-none bg-clayton-blue px-[30px] py-[13px] font-source-sans text-[18px] font-bold text-white disabled:pointer-events-none disabled:bg-[#888888] disabled:opacity-50"
                disabled={isFormSending}
              >
                {isFormSending ? 'SENDING . . .' : 'SUBMIT'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CtaContactForm;
